/* eslint-disable react/require-default-props */
import React, { useState, useCallback, useMemo, useId, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import Dropdown, { DropdownCloseContext } from './Dropdown';
import Typography from '../Typography';
import Button from '../Button';
import { Input } from '../Input';
import { FlexRow, FlexColumn } from '../GridsAndLayouts/GridsAndLayouts';
import { ChevronDownIcon, CloseIcon, SearchIcon } from '../Icons';

const SelectOptionRow = ({ option, selected, onPick, optionLayout = 'default', clearable = false }) => {
  const closeDropdown = useContext(DropdownCloseContext);
  const isRadioLayout = optionLayout === 'radio';
  const leadGraphic = option.icon ?? option.avatar;

  const handleClick = () => {
    if (isRadioLayout && clearable && selected) {
      onPick(null);
    } else {
      onPick(option.value);
    }
    closeDropdown();
  };

  return (
    <button
      type="button"
      role={isRadioLayout ? 'menuitemradio' : 'menuitem'}
      aria-checked={isRadioLayout ? selected : undefined}
      className={[
        'ds-dropdown-select__option',
        selected ? 'ds-dropdown-select__option--selected' : '',
        isRadioLayout ? 'ds-dropdown-select__option--layout-radio' : '',
        optionLayout === 'check' ? 'ds-dropdown-select__option--layout-check' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={handleClick}
    >
      {isRadioLayout && (
        <span className="ds-dropdown-select__radio-ring" aria-hidden>
          <span
            className={['ds-dropdown-select__radio-dot', selected ? 'ds-dropdown-select__radio-dot--on' : '']
              .filter(Boolean)
              .join(' ')}
          />
        </span>
      )}
      {leadGraphic != null && (
        <FlexRow className="ds-dropdown-select__avatar" align="center" justify="center" inline fullWidth={false}>
          {leadGraphic}
        </FlexRow>
      )}
      <FlexColumn className="ds-dropdown-select__option-text" align="start" fullWidth>
        <Typography variant="body1" component="span" className="ds-dropdown-select__option-title">
          {option.label}
        </Typography>
        {option.subtitle != null && option.subtitle !== '' && (
          <Typography variant="body2" color="muted" component="span" className="ds-dropdown-select__option-subtitle">
            {option.subtitle}
          </Typography>
        )}
      </FlexColumn>
    </button>
  );
};

SelectOptionRow.propTypes = {
  option: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.node.isRequired,
    subtitle: PropTypes.node,
    avatar: PropTypes.node,
    icon: PropTypes.node,
  }).isRequired,
  selected: PropTypes.bool.isRequired,
  onPick: PropTypes.func.isRequired,
  optionLayout: PropTypes.oneOf(['default', 'radio', 'check']),
  clearable: PropTypes.bool,
};

const optionShape = PropTypes.shape({
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  avatar: PropTypes.node,
  icon: PropTypes.node,
});

const groupShape = PropTypes.shape({
  id: PropTypes.string,
  label: PropTypes.string,
  options: PropTypes.arrayOf(optionShape).isRequired,
});

const Select = ({
  DropdownComponent = Dropdown,
  options = [],
  groups,
  value,
  defaultValue,
  onChange,
  placeholder = 'Select',
  searchPlaceholder = 'Search…',
  searchable = false,
  disabled = false,
  error = false,
  align = 'left',
  className = '',
  id,
  menuTitle,
  menuIcon,
  triggerLabel,
  /** `'radio'` — single-select list with radio indicators + dividers (Figma-style). */
  optionLayout = 'default',
  /** `'icon'` — square trigger with `menuIcon` only (toolbar / more menu). */
  triggerLayout = 'default',
  /** Accessible name for the trigger when `triggerLayout` is `icon` (recommended) or when the label is not visible. */
  triggerAriaLabel,
  /** `'actions'` — picking an option fires `onChange` but does not persist a selection in the trigger (command menu). */
  selectionBehavior = 'persist',
  /** When true, shows a clear control on the trigger and allows deselecting the current value (radio: re-click selected row). */
  clearable = false,
}) => {
  const intl = useIntl();
  const generatedId = useId();
  const controlId = id || generatedId;
  const searchInputId = `${controlId}-search`;
  // Lock controlled vs uncontrolled on first render (`null` = controlled, empty).
  const isControlledRef = useRef(null);
  if (isControlledRef.current === null) {
    isControlledRef.current = value !== undefined;
  }
  const isControlled = isControlledRef.current;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const selectedValue = isControlled ? value : internalValue;
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState('');

  const setValue = useCallback(
    (next) => {
      if (selectionBehavior === 'actions') {
        if (onChange) {
          onChange(next);
        }
        return;
      }
      if (!isControlled) {
        setInternalValue(next);
      }
      if (onChange) {
        onChange(next);
      }
    },
    [isControlled, onChange, selectionBehavior]
  );

  const hasGroups = Array.isArray(groups) && groups.length > 0;

  const flatOptions = useMemo(() => {
    if (hasGroups) {
      return groups.flatMap((group) => group.options || []);
    }
    return options;
  }, [groups, hasGroups, options]);

  const matchesSearchQuery = useCallback((opt, q) => {
    const labelStr = typeof opt.label === 'string' ? opt.label : '';
    const subStr = typeof opt.subtitle === 'string' ? opt.subtitle : '';
    return `${labelStr} ${subStr}`.toLowerCase().includes(q);
  }, []);

  const filteredOptions = useMemo(() => {
    if (hasGroups) {
      return flatOptions;
    }

    if (!searchable || !query.trim()) {
      return options;
    }

    const q = query.trim().toLowerCase();
    return options.filter((opt) => matchesSearchQuery(opt, q));
  }, [flatOptions, hasGroups, matchesSearchQuery, options, query, searchable]);

  const filteredGroups = useMemo(() => {
    if (!hasGroups) {
      return [];
    }

    if (!searchable || !query.trim()) {
      return groups;
    }

    const q = query.trim().toLowerCase();
    return groups
      .map((group) => ({
        ...group,
        options: (group.options || []).filter((opt) => matchesSearchQuery(opt, q)),
      }))
      .filter((group) => group.options.length > 0);
  }, [groups, hasGroups, matchesSearchQuery, query, searchable]);

  const selectedOption = useMemo(
    () => flatOptions.find((o) => String(o.value) === String(selectedValue)),
    [flatOptions, selectedValue]
  );

  const isIconTrigger = triggerLayout === 'icon';
  const triggerText =
    selectionBehavior === 'actions'
      ? triggerLabel ?? placeholder
      : triggerLabel ?? (selectedOption ? selectedOption.label : placeholder);
  const showAsValue = selectionBehavior === 'actions' ? Boolean(triggerLabel) : Boolean(triggerLabel || selectedOption);

  const hasSelection =
    selectionBehavior !== 'actions' && selectedValue !== undefined && selectedValue !== null && selectedValue !== '';
  const showClearControl = clearable && hasSelection && !disabled;

  const handleClear = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      setValue(null);
    },
    [setValue]
  );

  const trigger = (
    <FlexRow
      id={controlId}
      className={[
        'ds-dropdown-select__field',
        menuOpen && 'ds-dropdown-select__field--open',
        disabled && 'ds-dropdown-select__field--disabled',
        error && 'ds-dropdown-select__field--error',
        isIconTrigger && 'ds-dropdown-select__field--icon-only',
        showClearControl && 'ds-dropdown-select__field--has-clear',
      ]
        .filter(Boolean)
        .join(' ')}
      justify={isIconTrigger ? 'center' : 'between'}
      align="center"
    >
      {isIconTrigger ? (
        menuIcon && (
          <FlexRow
            className="ds-dropdown-select__menu-icon"
            align="center"
            justify="center"
            inline
            fullWidth={false}
            aria-hidden
          >
            {menuIcon}
          </FlexRow>
        )
      ) : (
        <>
          {menuIcon && (
            <FlexRow
              className="ds-dropdown-select__menu-icon"
              align="center"
              justify="center"
              inline
              fullWidth={false}
              aria-hidden
            >
              {menuIcon}
            </FlexRow>
          )}
          <Typography
            variant="body1"
            component="span"
            className={showAsValue ? 'ds-dropdown-select__value' : 'ds-dropdown-select__placeholder'}
          >
            {triggerText}
          </Typography>
          {showClearControl ? (
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              className="ds-dropdown-select__clear"
              icon={<CloseIcon size={12} strokeWidth={1.2} />}
              aria-label={intl.formatMessage({ id: 'clear' })}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={handleClear}
            />
          ) : null}
          <FlexRow
            className={`ds-dropdown-select__chevron ${menuOpen ? 'ds-dropdown-select__chevron--open' : ''}`}
            align="center"
            justify="center"
            inline
            fullWidth={false}
            aria-hidden
          >
            <ChevronDownIcon />
          </FlexRow>
        </>
      )}
    </FlexRow>
  );

  return (
    <DropdownComponent
      className={[
        'ds-dropdown--select',
        hasGroups && 'ds-dropdown--select-group',
        isIconTrigger && 'ds-dropdown--select-icon-trigger',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      trigger={trigger}
      align={align}
      disabled={disabled}
      triggerAriaLabel={triggerAriaLabel}
      onOpen={() => setMenuOpen(true)}
      onClose={() => {
        setMenuOpen(false);
        setQuery('');
      }}
    >
      {menuTitle && (
        <FlexRow
          className="ds-dropdown-select__header"
          align="center"
          justify="start"
          fullWidth
          onMouseDown={(e) => e.stopPropagation()}
        >
          <FlexRow className="ds-dropdown-select__title-row" align="center" inline fullWidth={false} gap={8}>
            {menuIcon && (
              <FlexRow
                className="ds-dropdown-select__header-menu-icon"
                align="center"
                justify="center"
                inline
                fullWidth={false}
                aria-hidden
              >
                {menuIcon}
              </FlexRow>
            )}
            <Typography
              component="span"
              variant="body1"
              fontWeight="semibold"
              className="ds-dropdown-select__menu-title"
            >
              {menuTitle}
            </Typography>
          </FlexRow>
        </FlexRow>
      )}
      {searchable && (
        <Typography component="label" variant="body1" className="ds-dropdown-select__search" htmlFor={searchInputId}>
          <FlexRow
            className="ds-dropdown-select__search-icon"
            align="center"
            justify="center"
            inline
            fullWidth={false}
            aria-hidden
          >
            <SearchIcon size={14} />
          </FlexRow>
          <Input
            id={searchInputId}
            type="search"
            className="ds-dropdown-select__search-input"
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            aria-label={searchPlaceholder}
          />
        </Typography>
      )}
      <FlexColumn
        className={[
          hasGroups ? 'ds-dropdown-select-group__list' : 'ds-dropdown-select__list',
          !hasGroups && optionLayout === 'radio' ? 'ds-dropdown-select__list--layout-radio' : '',
          !hasGroups && optionLayout === 'check' ? 'ds-dropdown-select__list--layout-check' : '',
          hasGroups && optionLayout === 'radio' ? 'ds-dropdown-select-group__list--layout-radio' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        role="presentation"
        align="start"
        fullWidth
      >
        {hasGroups
          ? filteredGroups.map((group, groupIndex) => (
              <FlexColumn
                key={group.id || group.label || groupIndex}
                className="ds-dropdown-select-group__section"
                align="start"
                fullWidth
              >
                {group.label ? (
                  <Typography
                    component="span"
                    variant="body2"
                    color="muted"
                    className="ds-dropdown-select-group__section-label"
                  >
                    {group.label}
                  </Typography>
                ) : null}
                <FlexColumn className="ds-dropdown-select-group__section-options" align="start" fullWidth>
                  {(group.options || []).map((option) => (
                    <SelectOptionRow
                      key={String(option.value)}
                      option={option}
                      selected={
                        selectionBehavior === 'actions' ? false : String(option.value) === String(selectedValue)
                      }
                      onPick={setValue}
                      optionLayout={optionLayout}
                      clearable={clearable}
                    />
                  ))}
                </FlexColumn>
              </FlexColumn>
            ))
          : filteredOptions.map((option) => (
              <SelectOptionRow
                key={String(option.value)}
                option={option}
                selected={selectionBehavior === 'actions' ? false : String(option.value) === String(selectedValue)}
                onPick={setValue}
                optionLayout={optionLayout}
                clearable={clearable}
              />
            ))}
      </FlexColumn>
    </DropdownComponent>
  );
};

Select.propTypes = {
  DropdownComponent: PropTypes.elementType,
  options: PropTypes.arrayOf(optionShape),
  groups: PropTypes.arrayOf(groupShape),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.oneOf([null])]),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.oneOf([null])]),
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  searchable: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  align: PropTypes.oneOf(['left', 'right', 'center']),
  className: PropTypes.string,
  id: PropTypes.string,
  menuTitle: PropTypes.string,
  menuIcon: PropTypes.node,
  triggerLabel: PropTypes.string,
  optionLayout: PropTypes.oneOf(['default', 'radio', 'check']),
  triggerLayout: PropTypes.oneOf(['default', 'icon']),
  triggerAriaLabel: PropTypes.string,
  selectionBehavior: PropTypes.oneOf(['persist', 'actions']),
  clearable: PropTypes.bool,
};

export default Select;
